import React, { createContext, useContext, ReactNode } from 'react';
import ApiClient from '../../lib/api-client';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { ICurrencyDTO } from '@common-frontend-submodule/interfaces/currency/icurrency.interface';
import { ICurrencyCreationDTO } from '@common-frontend-submodule/interfaces/currency/icurrency-creation.interface';
import { ICurrencyUpdateDTO } from '@common-frontend-submodule/interfaces/currency/icurrency-update.interface';
import { IGetCurrencysRequestDTO } from '@common-frontend-submodule/interfaces/currency/iget-currencys-request.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { CurrencyRoutes } from '@common-frontend-submodule/enums/routes/currency-routes.enum';

const apiClient = ApiClient.getInstance()

interface CurrencyContextProps {
  getById: (currencyId: string) => Promise<IApiResponseGenericDTO<ICurrencyDTO>>;
  getAll: () => Promise<IApiResponseGenericDTO<ICurrencyDTO[]>>;
  filter: (model: IGetCurrencysRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<ICurrencyDTO>>>;
  create: (model: ICurrencyCreationDTO) => Promise<IApiResponseGenericDTO<ICurrencyDTO>>;
  update: (currencyId: string, model: ICurrencyUpdateDTO) => Promise<IApiResponseGenericDTO<ICurrencyDTO>>;
  delete: (currencyId: string) => Promise<IApiResponseGenericDTO<boolean>>;
}

const CurrencyContext = createContext<CurrencyContextProps | undefined>(undefined);

export const CurrencyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getById = async (currencyId: string): Promise<IApiResponseGenericDTO<ICurrencyDTO>> => {
    return await apiClient.getRequest<ICurrencyDTO>(`${CurrencyRoutes.BasePrefix}/${currencyId}`);
  };

  const getAll = async (): Promise<IApiResponseGenericDTO<ICurrencyDTO[]>> => {
    return await apiClient.getRequest<ICurrencyDTO[]>(`${CurrencyRoutes.BasePrefix}/${CurrencyRoutes.All}`);
  };

  const filter = async (model: IGetCurrencysRequestDTO): Promise<IApiResponseGenericDTO<IPaginatedResultDTO<ICurrencyDTO>>> => {
    return await apiClient.postRequest<IGetCurrencysRequestDTO, IPaginatedResultDTO<ICurrencyDTO>>(
      `${CurrencyRoutes.BasePrefix}/${CurrencyRoutes.List}`,
      model
    );
  };

  const create = async (model: ICurrencyCreationDTO): Promise<IApiResponseGenericDTO<ICurrencyDTO>> => {
    return await apiClient.postRequest<ICurrencyCreationDTO, ICurrencyDTO>(CurrencyRoutes.BasePrefix, model);
  };

  const update = async (currencyId: string, model: ICurrencyUpdateDTO): Promise<IApiResponseGenericDTO<ICurrencyDTO>> => {
    return await apiClient.putRequest<ICurrencyUpdateDTO, ICurrencyDTO>(`${CurrencyRoutes.BasePrefix}/${currencyId}`, model);
  };

  const deleteCurrency = async (currencyId: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${CurrencyRoutes.BasePrefix}/${currencyId}`);
  };

  return (
    <CurrencyContext.Provider value={{ getById, getAll, filter, create, update, delete: deleteCurrency }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrencyStore = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrencyStore must be used within a CurrencyProvider');
  }
  return context;
};
